import { useDraggable, useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { SearchOutlined } from "@mui/icons-material";
import { Box, CircularProgress, InputAdornment, Paper, Stack, TextField, Typography } from "@mui/material";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useCatalogRecords } from "../hooks/useSectorWorkflow.query";
import { IFieldDefinition, IWorkflowBaseRecord, IWorkflowNodeConfig, WorkflowEditorMode } from "../interfaces";
import { RecordCard } from "./RecordCard";

interface IBindingCatalogPanelProps {
  node: IWorkflowNodeConfig;
  selectedRecordUid: string | null;
  onSelectRecord: (uid: string) => void;
  pageSize?: number;
  fields?: IFieldDefinition<any>[];
  width?: string | number;
  excludedBindingUids?: Set<string>;
  droppableId?: string;
  draggableSource?: "used" | "available";
}

interface IDraggableBindingRecordCardProps {
  id: string;
  source: "used" | "available";
  record: IWorkflowBaseRecord;
  children: ReactNode;
}

const DraggableBindingRecordCard = (props: IDraggableBindingRecordCardProps) => {
  const { id, source, record, children } = props;

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    data: {
      source,
      record,
    },
  });

  const style = {
    transform: transform ? CSS.Transform.toString(transform) : undefined,
    opacity: isDragging ? 0.6 : 1,
    cursor: "grab",
  };

  return (
    <Box ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </Box>
  );
};

export const BindingCatalogPanel = (props: IBindingCatalogPanelProps) => {
  const {
    node,
    selectedRecordUid,
    onSelectRecord,
    pageSize = 10,
    fields = [],
    width = "100%",
    excludedBindingUids,
    droppableId = "available-list",
    draggableSource = "available",
  } = props;

  const [accumulatedRecords, setAccumulatedRecords] = useState<IWorkflowBaseRecord[]>([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const bindingNode = useMemo<IWorkflowNodeConfig | null>(() => {
    if (!node.binding?.requestConfig?.uri) {
      return null;
    }

    return {
      key: `${node.key}-binding-${node.binding.key}`,
      title: node.binding.key,
      subtitle: "",
      group: node.group,
      mode: WorkflowEditorMode.Catalog,
      requestConfig: {
        uri: node.binding.requestConfig.uri,
      },
      fields: [],
      allowDelete: false,
    };
  }, [node.key, node.binding?.key, node.binding?.requestConfig?.uri, node.group]);

  const { setNodeRef: setDroppableNodeRef, isOver } = useDroppable({
    id: droppableId,
  });

  useEffect(() => {
    setPage(1);
  }, [search, bindingNode?.requestConfig?.uri]);

  useEffect(() => {
    setAccumulatedRecords([]);
  }, [search, bindingNode?.requestConfig?.uri]);

  const {
    records,
    page: currentPage,
    totalPages,
    isLoading,
    isFetching,
    error,
  } = useCatalogRecords({
    selectedNode: bindingNode ?? node,
    queryParams: {
      page,
      pageSize,
      name: search || undefined,
    },
    enabled: Boolean(bindingNode),
  });

  useEffect(() => {
    setAccumulatedRecords((current) => {
      if (page === 1) {
        return records;
      }

      const existingIds = new Set(current.map((item) => item.uid));
      const nextRecords = records.filter((item) => !existingIds.has(item.uid));

      return [...current, ...nextRecords];
    });
  }, [records, page]);

  const visibleRecords = useMemo(() => {
    if (!excludedBindingUids || excludedBindingUids.size === 0) {
      return accumulatedRecords;
    }

    return accumulatedRecords.filter((record) => !excludedBindingUids.has(record.uid));
  }, [accumulatedRecords, excludedBindingUids]);

  const hasMore = currentPage < totalPages;

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const threshold = 80;
    const isNearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;

    if (isNearBottom && hasMore && !isFetching) {
      setPage((current) => current + 1);
    }
  };

  if (!bindingNode) {
    return null;
  }

  return (
    <Box
      sx={{
        width,
        minWidth: 0,
        minHeight: 0,
        display: "flex",
        overflow: "hidden",
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 3,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderColor: isOver ? "primary.main" : "divider",
          boxShadow: isOver ? 2 : 0,
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        }}
      >
        <Typography variant="h6" fontWeight={700} sx={{ mb: 1, flexShrink: 0 }}>
          Available {node.binding?.key + "s" ?? "records"}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexShrink: 0 }}>
          Browse and search records
        </Typography>

        <TextField
          size="small"
          fullWidth
          value={search}
          placeholder="Search by name..."
          onChange={(event) => setSearch(event.target.value)}
          sx={{ mb: 2, flexShrink: 0 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Box
          ref={setDroppableNodeRef}
          onScroll={handleScroll}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            pr: 0.5,
            borderRadius: 2,
          }}
        >
          {isLoading ? (
            <Box sx={{ py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Loading records...
              </Typography>
            </Box>
          ) : null}

          {!isLoading && error ? (
            <Box sx={{ py: 3 }}>
              <Typography variant="body2" color="error">
                {error.message}
              </Typography>
            </Box>
          ) : null}

          {!isLoading && !error && visibleRecords.length === 0 ? (
            <Box sx={{ py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                No records found.
              </Typography>
            </Box>
          ) : null}

          {!isLoading && !error && visibleRecords.length > 0 ? (
            <Stack spacing={1.5} sx={{ pb: isFetching ? 1 : 0 }}>
              {visibleRecords.map((record) => (
                <DraggableBindingRecordCard key={record.uid} id={`${draggableSource}-${record.uid}`} source={draggableSource} record={record}>
                  <RecordCard
                    title={record.name ?? record.uid}
                    subtitle={record.description}
                    imageUrl={record.imageUrl}
                    record={record}
                    fields={fields}
                    selected={selectedRecordUid === record.uid}
                    onClick={() => onSelectRecord(record.uid)}
                  />
                </DraggableBindingRecordCard>
              ))}
            </Stack>
          ) : null}
        </Box>

        {isFetching && !isLoading ? (
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 1.5,
              borderTop: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <CircularProgress size={22} />
          </Box>
        ) : null}
      </Paper>
    </Box>
  );
};
